import React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, Chip, InputAdornment, Stack, TextField } from '@mui/material';

import { LabelResult } from 'stores/LabelStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';

export type ArticleSearchState = {
  keyword: string;
  labelIds: string[];
  authorUserIds: string[];
};

type Props = {
  value: ArticleSearchState;
  onChange: (next: ArticleSearchState) => void;
  /** ラベル候補（API6207） */
  labels: LabelResult[];
  /** 著者候補。ゲスト時は空配列（著者絞り込みはログイン時のみ意味を持つ） */
  users: UserResult[];
};

/**
 * 記事一覧の検索バー。
 * キーワード（部分一致）+ Labels（OR）+ Author（OR）。
 * 変更は onChange で親に通知し、親が API 再取得を行う。
 */
const ArticleSearchBar: React.FC<Props> = ({ value, onChange, labels, users }) => {
  const selectedLabels = labels.filter((l) => value.labelIds.includes(l.labelId));
  const selectedUsers = users.filter((u) => value.authorUserIds.includes(u.userId));

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      sx={{
        alignItems: { md: 'center' },
      }}
    >
      <TextField
        size="small"
        placeholder="キーワードで検索..."
        value={value.keyword}
        onChange={(e) => onChange({ ...value, keyword: e.target.value })}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'action.hover',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 1, borderColor: 'primary.main' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        size="small"
        options={labels}
        value={selectedLabels}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.labelId === v.labelId}
        disableCloseOnSelect
        onChange={(_, v) => onChange({ ...value, labelIds: v.map((l) => l.labelId) })}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
          return (
            <Box
              component="li"
              key={String(key)}
              {...rest}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: option.color,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              {option.name}
            </Box>
          );
        }}
        limitTags={1}
        renderTags={(vals, getTagProps) =>
          vals.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={option.name}
                size="small"
                sx={{
                  borderRadius: 1.5,
                  color: '#fff',
                  bgcolor: option.color,
                  maxWidth: 120,
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Labels"
            placeholder={selectedLabels.length === 0 ? '絞り込み' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, flexWrap: 'nowrap' } }}
          />
        )}
        sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}
      />

      {/* 著者絞り込みはユーザー候補があるとき（ログイン時）のみ表示 */}
      {users.length > 0 && (
        <Autocomplete
          multiple
          size="small"
          options={users}
          value={selectedUsers}
          getOptionLabel={(o) => o.userName}
          isOptionEqualToValue={(o, v) => o.userId === v.userId}
          disableCloseOnSelect
          limitTags={1}
          onChange={(_, v) => onChange({ ...value, authorUserIds: v.map((u) => u.userId) })}
          renderTags={(vals, getTagProps) =>
            vals.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option.userName}
                  size="small"
                  sx={{ borderRadius: 1.5, maxWidth: 120 }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Author"
              placeholder={selectedUsers.length === 0 ? '絞り込み' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, flexWrap: 'nowrap' } }}
            />
          )}
          sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}
        />
      )}
    </Stack>
  );
};

export default ArticleSearchBar;
